import React, { useEffect, useRef, useState } from "react";

import { Row } from "react-bootstrap";

import { useTypedParams } from "react-router-typesafe-routes/dom";
import { ROUTES } from "src/routes";

import {
  FinanceHold,
  IdCheckStatus,
  ParticipantStatus,
  ParticipantTypes,
  useGetAccountDetailsByParticipantIdQuery,
  useGetUserMfaPreferenceQuery,
} from "@api/graphql";
import { isBankDetailsValid } from "@common/bank_details";
import { AccountDetailsAccordion } from "@components/account_profile/account_details/AccountDetailsAccordion";
import BankDetailsAccordion from "@components/account_profile/account_details/BankDetailsAccordion";
import ContactDetailsAccordion from "@components/account_profile/account_details/ContactDetailsAccordion";
import FinanceContactAccordion from "@components/account_profile/account_details/FinanceContactAccordion";
import PseudonymsAccordion from "@components/account_profile/account_details/PseudonymsAccordion";
import RelatedUsersAccordion from "@components/account_profile/account_details/RelatedUsersAccordion";
import { Loading } from "@components/loading";
import { NotificationTile } from "@components/notification_tile/NotificationTile";
import { ProtectedComponent, useIsAuthUserAllowed } from "@components/permission_control/ProtectedComponent";
import { useAuthenticationContext } from "@context/authentication";
import { Role } from "@roles";
import { isArtCentre, isArtistOrBeneficiary, isCMO } from "@util/index";
import { NOTIFICATION_TILES } from "@util/notificationTile";

import IdCheckModal from "./IdCheckModal";
import { InviteParticipantModal } from "./InviteParticipantModal";
import { InternalUserInfoAccordion } from "./account_details/InternalUserInfoAccordion";
import { NotesAccordion } from "./account_details/NotesAccordion";

const statusMap: { [key: string]: string } = {
  DECEASED: "Deceased",
  BANK_DETAILS_REQUIRED: "Bank details required",
  ENTITLEMENT_ISSUES: "Entitlement issues",
  INCARCERATED: "Incarcerated",
  BOUNCED_PAYMENT: "Bounced",
  OTHER: "Other",
};

const AccountDetails = () => {
  const [showIdCheckModal, setShowIdCheckModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);

  const { auth } = useAuthenticationContext();

  const { participantId } = useTypedParams(ROUTES.Profile.AccountDetails);

  const bankDetailsRef = useRef<HTMLDivElement>(null);

  const { data, loading, refetch } = useGetAccountDetailsByParticipantIdQuery({
    variables: { participantId: participantId ?? -1 },
    skip: !participantId,
  });

  const { data: mfaData, loading: mfaLoading, refetch: refetchMfa } = useGetUserMfaPreferenceQuery();
  // Apollo caches queries automatically hence causing the display of outdated cached data when a previously recently queried page is re-visited but with changed data.
  // https://www.apollographql.com/docs/react/data/queries#updating-cached-query-results
  useEffect(() => {
    refetch();
    refetchMfa();
  }, [refetch, refetchMfa]);

  const isInternalUser = useIsAuthUserAllowed([Role.VisualArtistAdmin, Role.MemberServices]);

  if (loading || mfaLoading) {
    return <Loading />;
  }

  const {
    account,
    business_and_tax,
    contact,
    bank_details,
    finance_contact,
    related_users,
    pseudonyms,
    participant_types = [],
    participant_is_reporter,
    participant_is_recipient,
    participant_notes,
    internal_user_info,
  } = data?.getAccountDetailsByParticipantId || {};

  const { preference: mfaPreferences } = mfaData?.getUserMfaPreference || {};

  if (!account || !business_and_tax || !contact || !bank_details || !finance_contact || !related_users) {
    return <>Missing data</>;
  }

  const isParticipantArtistOrBeneficiary = isArtistOrBeneficiary(participant_types);

  const showIdCheckNotification = Boolean(
    isParticipantArtistOrBeneficiary &&
      account.status === ParticipantStatus.PENDING_APPROVAL &&
      (!internal_user_info?.id_check_status || internal_user_info?.id_check_status === IdCheckStatus.INCOMPLETE),
  );

  const showBankDetailsNotification = Boolean(
    (isParticipantArtistOrBeneficiary || isArtCentre(participant_types) || isCMO(participant_types)) &&
      participant_is_recipient &&
      account.status === ParticipantStatus.ACTIVE &&
      !bank_details.bank_type &&
      !isBankDetailsValid(bank_details),
  );

  const scrollToBankDetails = () => {
    bankDetailsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const showPaymentHoldNotification = Boolean(
    account.status === ParticipantStatus.ACTIVE &&
      participant_is_recipient &&
      internal_user_info?.finance_hold !== FinanceHold.NO_HOLD,
  );

  // The account notification should be shown for an artist or benificiary type account if participant status is incomplete and we have never invited them before.
  // The account notification should be shown for cmo, art center or amp type account if participant status is incomplete.
  const showAccountNotification =
    account.status === ParticipantStatus.INCOMPLETE &&
    (!isParticipantArtistOrBeneficiary || contact.date_created === null);

  const getStatusDisplay = (status: string): string => {
    return statusMap[status];
  };

  const handleOpenModal = () => setShowInviteModal(true);
  const handleCloseModal = () => setShowInviteModal(false);

  return (
    <>
      <IdCheckModal
        show={showIdCheckModal}
        setShow={setShowIdCheckModal}
        name={`${contact.first_name} ${contact.last_name}`}
        participant_id={participantId ?? -1}
        refetch={refetch}
      />
      {showIdCheckNotification && (
        <Row className="mb-4">
          <NotificationTile
            title={NOTIFICATION_TILES.id_check.title}
            buttonText={NOTIFICATION_TILES.id_check.buttonText}
            buttonAction={() => setShowIdCheckModal(true)}
          />
        </Row>
      )}
      {showBankDetailsNotification && (
        <Row className="mb-4">
          <NotificationTile
            title={NOTIFICATION_TILES.bank_details.title}
            buttonText={NOTIFICATION_TILES.bank_details.buttonText}
            buttonAction={scrollToBankDetails}
          />
        </Row>
      )}
      {showPaymentHoldNotification && (
        <ProtectedComponent auth={auth} requiredGroups={[Role.VisualArtistAdmin, Role.MemberServices]}>
          <Row className="mb-4">
            <NotificationTile
              title={NOTIFICATION_TILES.payment_hold.title}
              buttonText={
                internal_user_info?.finance_hold ? getStatusDisplay(internal_user_info?.finance_hold) : "Unknown status"
              }
            />
          </Row>
        </ProtectedComponent>
      )}

      {showAccountNotification && (
        <ProtectedComponent auth={auth} requiredGroups={[Role.VisualArtistAdmin, Role.MemberServices]}>
          <Row className="mb-4">
            <NotificationTile
              title={NOTIFICATION_TILES.account.title}
              buttonText={NOTIFICATION_TILES.account.buttonText}
              buttonAction={handleOpenModal}
            />
          </Row>
        </ProtectedComponent>
      )}

      <AccountDetailsAccordion
        account={account}
        business_and_tax={business_and_tax}
        participant_types={participant_types}
        participant_is_reporter={participant_is_reporter}
        participant_is_recipient={participant_is_recipient}
        participant_id={participantId}
      />
      <ContactDetailsAccordion
        contact={contact}
        isInternalUser={isInternalUser}
        participantTypes={participant_types}
        participantId={participantId}
        mfaPreference={mfaPreferences}
        accountStatus={account.status}
        onUpdate={refetch}
      />
      <FinanceContactAccordion
        financeContact={finance_contact}
        participantTypes={participant_types}
        participantId={participantId}
        participant_is_reporter={participant_is_reporter}
        accountStatus={account.status}
      />
      <Row ref={bankDetailsRef}>
        <BankDetailsAccordion
          bankDetails={bank_details}
          participantTypes={participant_types}
          onUpdate={() => refetch()}
        />
      </Row>
      <RelatedUsersAccordion
        relatedUsers={related_users}
        isInternalUser={isInternalUser}
        participantTypes={participant_types}
        refetchAccountDetails={refetch}
      />
      <PseudonymsAccordion participantTypes={participant_types} pseudonyms={pseudonyms} participantId={participantId} />
      {internal_user_info && (
        <ProtectedComponent auth={auth} requiredGroups={[Role.VisualArtistAdmin, Role.MemberServices, Role.Data]}>
          <InternalUserInfoAccordion
            userInfo={internal_user_info}
            accountStatus={account.status}
            participantTypes={participant_types?.filter((type): type is ParticipantTypes => type !== undefined)}
          />
        </ProtectedComponent>
      )}
      <ProtectedComponent auth={auth} requiredGroups={[Role.VisualArtistAdmin, Role.MemberServices, Role.Data]}>
        <NotesAccordion notes={participant_notes || []} />
      </ProtectedComponent>

      {!participant_is_reporter && !participant_is_recipient && <>You do not have permission to view this page</>}

      <InviteParticipantModal
        isOpen={showInviteModal}
        onClose={handleCloseModal}
        fullName={contact.full_name || ""}
        participantId={participantId}
      />
    </>
  );
};
export default AccountDetails;
